import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useAuth from "../../../../../../hooks/useAuth";
import StepIndicator from "./StepIndicator";
import Step1Position from "./Step1Position";
import Step2Associate from "./Step2Associate";
import Step3FTE from "./Step3FTE";
import Step4Review from "./Step4Review";
import SuccessScreen from "./SuccessScreen";
import { getOpenPositions } from "../../../../../../api/positions";
import {
  getAvailableAssociates,
  getAssociateLiveFte,
} from "../../../../../../api/associates";
import {
  submitAllocationRequest,
  resubmitAllocationRequest,
  getAllocationById,
  updateAllocationRequest,
  amendAllocationRequest,
} from "../../../../../../api/macLeader";
import MoveWizard from "./MoveWizard";
import { SKILLS_REQUIRED_MESSAGE } from "./constants";

const AllocationWizard = ({
  open,
  onClose,
  onSubmitted,
  editRow = null,
  readOnly = false,
  sowId = null,
}) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";
  const { user } = useAuth();

  const isEditMode = !!editRow;
  const isResubmit = isEditMode && editRow?.status === "REJECTED" && !readOnly;

  const [step, setStep] = useState(1);

  const [positions, setPositions] = useState([]);
  const [posLoading, setPosLoading] = useState(false);
  const [selectedPos, setSelectedPos] = useState(null);

  const [associates, setAssociates] = useState([]);
  const [assocLoading, setAssocLoading] = useState(false);
  const [selectedAssoc, setSelectedAssoc] = useState(null);

  const [fte, setFte] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});
  const [fteLoading, setFteLoading] = useState(false);
  const [overlapError, setOverlapError] = useState(null);

  const [moveOpen, setMoveOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    setApiError(null);
    setErrors({});
    setSubmitted(false);
    setOverlapError(null);
    setAssociates([]);

    if (isEditMode) {
      setStep(3);

      // Seed UI immediately with whatever the caller passed in
      setSelectedPos({
        positionId: null,
        positionCode: editRow.position?.positionCode,
        roleTitle: editRow.position?.roleTitle,
        sowName: editRow.position?.sowName,
        positionType: editRow.position?.positionType ?? "BIL",
        levelCode: editRow.associate?.levelCode,
        createdAt: editRow.position?.startDate ?? null, // ← ADD
        endDate: editRow.position?.endDate ?? null, // ← ADD
        accountName: editRow.accountName ?? editRow.position?.accountName ?? null,
      });
      setSelectedAssoc({
        associateId: null,
        fullName: editRow.associate?.fullName,
        levelCode: editRow.associate?.levelCode,
        usedFte: null,
        availableFte: null,
        skills: editRow.associate?.skills ?? editRow.allAssociateSkills ?? editRow.skills ?? [],
      });

      setFte(editRow.fte ?? null);
      setSelectedSkills(editRow.deployedSkillNames ?? editRow.skills ?? []);
      setStartDate(editRow.startDate ?? "");
      setEndDate(editRow.endDate ?? "");
      setNotes(editRow.notes ?? "");

      // --- FIX: resolve the real numeric/string id from whichever field is present ---
      const resolvedId =
        editRow.id ?? editRow.allocationId ?? editRow.requestId ?? null;

      if (!resolvedId) {
        console.error(
          "AllocationWizard: editRow has no resolvable id — check the field passed from the table.",
          editRow,
        );
        return;
      }

      setFteLoading(true);
      getAllocationById(resolvedId)
        .then((full) => {
  setSelectedPos((prev) => ({
    ...prev,
    positionId: full.positionId,
  }));

  setSelectedAssoc((prev) => ({
    ...prev,
    associateId: full.associateId,
    usedFte: 0,
    availableFte: 1.0 - (editRow.fte ?? 0),
    skills: full.associateSkills ?? [],
    currentMacName: full.currentMacName ?? null,
  }));

  // deployedSkills from API is [{skillId, skillName}] — extract just the IDs
  const deployedSkillIds = (full.deployedSkills ?? []).map(s => s.skillId);
  setSelectedSkills(deployedSkillIds);
})
        .catch((err) => {
          console.error("Failed to load full request / live FTE:", err);
          setSelectedPos((prev) => ({ ...prev, positionId: resolvedId }));
          setSelectedAssoc((prev) => ({
            ...prev,
            associateId: resolvedId,
            usedFte: 0,
            availableFte: 1.0,
          }));
        })
        .finally(() => setFteLoading(false));
    } else {
      setStep(1);
      setSelectedPos(null);
      setSelectedAssoc(null);
      setFte(null);
      setSelectedSkills([]);
      setStartDate("");
      setEndDate("");
      setNotes("");
    }
  }, [open, isEditMode, editRow]);

  useEffect(() => {
    if (!open || isEditMode) return;
    setPosLoading(true);
    getOpenPositions(sowId ? { sowId } : {}) // ← pass sowId if present
      .then(setPositions)
      .catch((err) => console.error("Failed to load positions:", err))
      .finally(() => setPosLoading(false));
  }, [open, isEditMode, sowId]); // ← add sowId to deps

  useEffect(() => {
    if (isEditMode || step !== 2 || associates.length) return;
    setAssocLoading(true);
    getAvailableAssociates()
      .then(setAssociates)
      .catch((err) => console.error("Failed to load associates:", err))
      .finally(() => setAssocLoading(false));
  }, [step, isEditMode]);

  const skills = selectedAssoc?.skills || [];

  // Only ids visible in the associate's profile count, so a stale inherited
  // id can't satisfy the rule while nothing on screen is ticked.
  const pickedSkillIds = skills
    .filter((sk) => selectedSkills.includes(sk.skillId) || selectedSkills.includes(sk.skillName))
    .map((sk) => sk.skillId)
    .filter(Boolean);
  const skillsMissing = skills.length > 0 && pickedSkillIds.length === 0;

  const toggleSkill = (sk) => {
  if (readOnly) return;
  setSelectedSkills((prev) =>
    prev.includes(sk) ? prev.filter((s) => s !== sk) : [...prev, sk]
  );
};

  const canProceed = () => {
    if (readOnly) return false;
    if (step === 1) return !!selectedPos;
    if (step === 2) return !!selectedAssoc;
    if (step === 3) {
      return fte !== null && !!startDate && !!endDate && !fteLoading && !overlapError && !skillsMissing;
    }
    return true;
  };

  const handleNext = () => {
    if (canProceed()) setStep((s) => s + 1);
  };
  const handleBack = () => {
    if (isEditMode && step <= 3) return;
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    try {
      setSubmitting(true);
      setApiError(null);

      const resolvedId =
        editRow?.id ?? editRow?.allocationId ?? editRow?.requestId ?? null;

      if (isResubmit) {
        // REJECTED → resubmit
        await resubmitAllocationRequest(resolvedId, {
          fte: fte ?? undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          notes: notes || undefined,
        });
      } else if (isEditMode && editRow?.status === "ACTIVE") {
        // ACTIVE → amend (creates new request row)
        await amendAllocationRequest(resolvedId, {
          fte,
          startDate,
          endDate,
          notes,
          deployedSkillIds: pickedSkillIds,
        });
      } else if (isEditMode) {
        // SUBMITTED or PMO_APPROVED → patch in place
        await updateAllocationRequest(resolvedId, {
          fte: fte ?? undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          notes: notes || undefined,
          deployedSkillIds: pickedSkillIds,
        });
      } else {
        await submitAllocationRequest({
          positionId: selectedPos.positionId,
          associateId: selectedAssoc.associateId,
          fte,
          startDate: startDate || null,
          endDate: endDate || null,
          notes: notes || null,
          deployedSkillIds: pickedSkillIds,
        });
      }

      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        "Something went wrong. Please try again.";
      setApiError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isAmend = isEditMode && editRow?.status === "ACTIVE" && !readOnly;
  const saveLabel = isResubmit
    ? "Resubmit"
    : isAmend
      ? "Submit Amendment"
      : isEditMode
        ? "Save Changes"
        : "Submit for Approval";
  const savingLabel = isResubmit
    ? "Resubmitting..."
    : isAmend
      ? "Submitting Amendment..."
      : isEditMode
        ? "Saving..."
        : "Submitting...";

  const headerTitle = readOnly
    ? "View Allocation"
    : isResubmit
      ? "Resubmit Request"
      : isAmend
        ? "Amend Allocation"
        : isEditMode
          ? "Edit Allocation"
          : "New Allocation Request";

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1200,
        }}
      />

      {/* Drawer */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "480px",
          height: "100vh",
          background: t.surface,
          borderLeft: `1px solid ${t.border2}`,
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: `1px solid ${t.border2}`,
            flexShrink: 0,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "15px",
                  fontWeight: 700,
                  color: t.text,
                }}
              >
                {headerTitle}
              </Typography>
              {readOnly && (
                <Box
                  sx={{
                    background: "rgba(120,120,120,0.12)",
                    border: "1px solid rgba(120,120,120,0.25)",
                    borderRadius: "3px",
                    padding: "2px 7px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "8px",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: t.text3,
                    }}
                  >
                    Read Only
                  </Typography>
                </Box>
              )}
            </Box>
            {isEditMode && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "2px" }}>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent }}>
                  {editRow.requestCode}
                </Typography>
                {isEditMode && editRow?.status === "ACTIVE" && !readOnly && (
                  <Box
                    onClick={() => setMoveOpen(true)}
                    sx={{
                      padding: "2px 10px", borderRadius: "3px", cursor: "pointer",
                      border: "1px solid rgba(214,223,39,0.35)",
                      background: "rgba(214,223,39,0.08)",
                      fontFamily: "'DM Mono', monospace", fontSize: "8px",
                      color: t.gold, fontWeight: 600, letterSpacing: "0.08em",
                      transition: "all 0.15s", "&:hover": { background: "rgba(214,223,39,0.15)" },
                    }}
                  >
                    ⇄ MOVE
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Box
            onClick={onClose}
            sx={{
              cursor: "pointer",
              color: t.text3,
              fontSize: "18px",
              lineHeight: 1,
              padding: "4px",
              "&:hover": { color: t.text },
            }}
          >
            ✕
          </Box>
        </Box>

        {/* Rejection reason banner */}
        {(isResubmit || readOnly) && editRow?.rejectionReason && (
          <Box
            sx={{
              margin: "12px 24px 0",
              padding: "10px 14px",
              background: "rgba(200,64,64,0.08)",
              border: "1px solid rgba(200,64,64,0.28)",
              borderRadius: "4px",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "8px",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#c84040",
                mb: "4px",
              }}
            >
              Rejection Reason
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "12px",
                color: t.text2,
                lineHeight: 1.5,
              }}
            >
              {editRow.rejectionReason}
            </Typography>
          </Box>
        )}

        {/* Locked position + associate pill */}
        {isEditMode && (
          <Box
            sx={{
              margin: "12px 24px 0",
              padding: "10px 14px",
              background: t.surface2,
              border: `1px solid ${t.border2}`,
              borderRadius: "4px",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", gap: "20px" }}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "8px",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: t.text3,
                    mb: "2px",
                  }}
                >
                  Position
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "10px",
                    color: t.accent,
                  }}
                >
                  {editRow.position?.positionCode ?? "—"}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: t.text2 }}>
  {editRow.position?.roleTitle ?? "—"}
</Typography>
<Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, mt: "2px" }}>
  {editRow.accountName ?? "—"}
</Typography>
              </Box>
              <Box sx={{ width: "1px", background: t.border2 }} />
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "8px",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: t.text3,
                    mb: "2px",
                  }}
                >
                  Associate
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: t.text }}
                >
                  {editRow.associate?.fullName ?? "—"}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: t.text3 }}>
                  Level {editRow.associate?.levelCode ?? "—"}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "8px",
                color: t.text3,
                mt: "8px",
                letterSpacing: "0.04em",
              }}
            >
              {readOnly
                ? "This request is read-only and cannot be modified."
                : "Position and associate are locked — only FTE, dates and notes can be changed."}
            </Typography>
          </Box>
        )}

        {/* Scrollable body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "20px 24px",
            minHeight: 0,
          }}
        >
          {submitted ? (
            <SuccessScreen
              onClose={onClose}
              isEdit={isEditMode}
              isResubmit={isResubmit}
            />
          ) : (
            <>
              <StepIndicator current={step} isEditMode={isEditMode} />

              {!isEditMode && step === 1 && (
                <Step1Position
                  positions={positions}
                  selected={selectedPos}
                  onSelect={readOnly ? undefined : setSelectedPos}
                  loading={posLoading}
                  readOnly={readOnly}
                />
              )}
              {!isEditMode && step === 2 && (
                <Step2Associate
                  associates={associates}
                  selected={selectedAssoc}
                  onSelect={readOnly ? undefined : setSelectedAssoc}
                  selectedPosition={selectedPos}
                  loading={assocLoading}
                  readOnly={readOnly}
                />
              )}

              {step === 3 && (
                <>
                  <Step3FTE
                    associate={selectedAssoc}
                    position={selectedPos}
                    fte={fte}
                    skillsLoading={fteLoading}
                    onFteChange={readOnly ? undefined : setFte}
                    skills={skills}
                    selectedSkills={selectedSkills}
                    onSkillToggle={toggleSkill}
                    skillsError={skillsMissing && !readOnly ? SKILLS_REQUIRED_MESSAGE : null}
                    startDate={startDate}
                    onStartChange={readOnly ? undefined : setStartDate}
                    endDate={endDate}
                    onEndChange={readOnly ? undefined : setEndDate}
                    notes={notes}
                    onNotesChange={readOnly ? undefined : setNotes}
                    errors={errors}
                    readOnly={readOnly}
                    currentFte={isEditMode ? (editRow?.fte ?? 0) : 0}
                    excludeRequestId={isEditMode ? (editRow?.requestId ?? editRow?.id ?? editRow?.allocationId ?? null) : null}
                    excludeParentRequestId={isEditMode ? (editRow?.parentRequestId ?? null) : null}
                    onOverlapError={setOverlapError}
                  />

                </>
              )}

              {step === 4 && (
                <Step4Review
                  position={selectedPos}
                  associate={selectedAssoc}
                  fte={fte}
                  selectedSkills={selectedSkills}
                  skills={skills}
                  startDate={startDate}
                  endDate={endDate}
                  notes={notes}
                  isEditMode={isEditMode}
                  readOnly={readOnly}
                />
              )}

              {apiError && (
                <Box
                  sx={{
                    mt: "12px",
                    padding: "8px 12px",
                    background: "rgba(200,64,64,0.08)",
                    border: "1px solid rgba(200,64,64,0.3)",
                    borderRadius: "3px",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      color: "#c84040",
                    }}
                  >
                    {apiError}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        {!submitted && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: readOnly ? "flex-end" : "space-between",
              padding: "14px 24px",
              borderTop: `1px solid ${t.border2}`,
              flexShrink: 0,
            }}
          >
            <Box
              onClick={onClose}
              sx={{
                padding: "8px 20px",
                borderRadius: "3px",
                cursor: "pointer",
                border: `1px solid ${t.border2}`,
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                color: t.text2,
                transition: "all 0.15s",
                "&:hover": { borderColor: t.border3, color: t.text },
              }}
            >
              {readOnly ? "Close" : "Cancel"}
            </Box>

            {!readOnly && (
              <Box sx={{ display: "flex", gap: "8px" }}>
                {((isEditMode && step > 3) || (!isEditMode && step > 1)) && (
                  <Box
                    onClick={handleBack}
                    sx={{
                      padding: "8px 18px",
                      borderRadius: "3px",
                      cursor: "pointer",
                      border: `1px solid ${t.border2}`,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      color: t.text2,
                      transition: "all 0.15s",
                      "&:hover": { borderColor: t.border3, color: t.text },
                    }}
                  >
                    ← Back
                  </Box>
                )}

                {step < 4 ? (
                  <Box
                    onClick={handleNext}
                    sx={{
                      padding: "8px 24px",
                      borderRadius: "3px",
                      cursor: canProceed() ? "pointer" : "not-allowed",
                      background: canProceed() ? t.accent : t.surface3,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: canProceed()
                        ? isDark
                          ? "#060E0D"
                          : "#fff"
                        : t.text3,
                      transition: "all 0.15s",
                      "&:hover": canProceed() ? { opacity: 0.88 } : {},
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Next →
                  </Box>
                ) : (
                  <Box
                    onClick={submitting ? undefined : handleSubmit}
                    sx={{
                      padding: "8px 24px",
                      borderRadius: "3px",
                      cursor: submitting ? "not-allowed" : "pointer",
                      background: submitting ? t.surface3 : t.accent,
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: submitting ? t.text3 : isDark ? "#060E0D" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.15s",
                      "&:hover": submitting ? {} : { opacity: 0.88 },
                    }}
                  >
                    {submitting && (
                      <CircularProgress size={10} sx={{ color: "inherit" }} />
                    )}
                    {submitting ? savingLabel : saveLabel}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <MoveWizard
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        onSubmitted={() => { setMoveOpen(false); onSubmitted?.(); onClose(); }}
        sourceRequest={editRow ? {
          requestId:          editRow.requestId ?? editRow.id ?? editRow.allocationId,
          requestCode:        editRow.requestCode,
          positionType:       editRow.positionType ?? editRow.position?.positionType,
          positionCode:       editRow.position?.positionCode,
          roleTitle:          editRow.position?.roleTitle,
          associateName:      editRow.associate?.fullName,
          associateLevelCode: editRow.associate?.levelCode,
          fte:                editRow.fte,
          endDate:            editRow.endDate,
          deployedSkills:     (editRow.deployedSkillNames ?? []).map((name, i) => ({ skillId: name, skillName: name })),
          associateSkills:    selectedAssoc?.skills ?? editRow.associate?.skills ?? [],
          deployedSkills:     selectedSkills.map((id) => ({ skillId: id, skillName: id })),
        } : null}
      />
    </>
  );
};

export default AllocationWizard;
